import * as THREE from 'three'
import * as Sentry from '@sentry/react'
import { SENTRY_BREADCRUMB_CATEGORY } from 'Renderer/constants'

export default class TextureHelper {
    constructor(renderer) {
        this.renderer = renderer
        this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.0, 1000)
        this.scene = new THREE.Scene()
        this.material = new THREE.ShaderMaterial({
          uniforms: {
            map: { value: null },
          },
          vertexShader: `
            varying vec2 vUv;
            void main() {
                vUv = uv;
                gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
            }
          `,
          fragmentShader: `
            uniform sampler2D map;
            varying vec2 vUv;
            void main() {
                gl_FragColor = texture2D(map, vUv);
                // gl_FragColor = vec4(1.0, 0.0, 0.0, 1.0);
            }
          `,
          side: THREE.DoubleSide,
        })

        this.planeMesh = new THREE.Mesh(new THREE.PlaneBufferGeometry(2, 2), this.material)
        this.scene.add(this.planeMesh)

        this.renderTargets = new Map()
        this.disposeCallback = this.disposeCallback.bind(this)
    }

    getResizedCopy(texture, width, height) {
        if (!texture) {
            return null
        }
        if (!this.renderer) {
            return null
        }
        const threeRenderer = this.renderer.ThreeWGLrenderer
        if (!threeRenderer) {
            return null
        }

        this.material.uniforms.map.value = texture
        this.material.map = texture

        const renderTarget = new THREE.WebGLRenderTarget(width, height)
        renderTarget.texture.name = `${texture.name}-resized`
        renderTarget.texture.encoding = texture.encoding

        const currentRenderTarget = threeRenderer.getRenderTarget()

        threeRenderer.setRenderTarget(renderTarget)
        threeRenderer.clear()

        threeRenderer.render(this.scene, this.camera)

        threeRenderer.setRenderTarget(currentRenderTarget)

        this.material.uniforms.map.value = null
        this.material.map = null

        renderTarget.texture.addEventListener('dispose', this.disposeCallback)

        this.renderTargets.set(renderTarget.texture.uuid, renderTarget)

        return renderTarget.texture
    }

    disposeCallback(event) {
      const texture = event.target
      const renderTarget = this.renderTargets.get(texture.uuid)
      if (renderTarget) {
        this.renderTargets.delete(texture.uuid)
        renderTarget.dispose()
      }
      texture.removeEventListener('dispose', this.disposeCallback)
    }

    readPixels(texture) {
      if (!texture || !texture.image) {
        return null
      }
      if (!this.renderer) {
        return null
      }
      const threeRenderer = this.renderer.ThreeWGLrenderer
      if (!threeRenderer) {
        return null
      }

      this.material.uniforms.map.value = texture
      this.material.map = texture

      const { width, height } = texture.image

      const retBuffer = new Uint8Array(width * height * 4)

      const renderTarget = new THREE.WebGLRenderTarget(width, height)
      renderTarget.texture.name = `${texture.name}-resized`
      renderTarget.texture.encoding = texture.encoding

      const currentRenderTarget = threeRenderer.getRenderTarget()

      threeRenderer.setRenderTarget(renderTarget)
      threeRenderer.clear()

      threeRenderer.render(this.scene, this.camera)

      threeRenderer.setRenderTarget(currentRenderTarget)

      this.material.uniforms.map.value = null
      this.material.map = null

      threeRenderer.readRenderTargetPixels(renderTarget, 0, 0, width, height, retBuffer)
      renderTarget.dispose()

      return { data: retBuffer, width, height }
    }

    testFloatRT() {
      if (!this.renderer) {
          return false
      }
      const threeRenderer = this.renderer.ThreeWGLrenderer
      if (!threeRenderer) {
          return false
      }

      const context = threeRenderer.getContext()
      if (!context) {
          return false
      }

      const size = 2
      const renderTarget = new THREE.WebGLRenderTarget(size, size, {
          minFilter: THREE.LinearFilter,
          magFilter: THREE.LinearFilter,
          format: THREE.RGBAFormat,
          type: THREE.FloatType,
      })

      const material = new THREE.ShaderMaterial({
        vertexShader: `
          void main() {
              gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
          }
        `,
        fragmentShader: `
          void main() {
              gl_FragColor = vec4(1.0, 0.5, 0.25, 1.0);
          }
        `,
        side: THREE.DoubleSide,
      })

      const currentRenderTarget = threeRenderer.getRenderTarget()
      const currentMaterial = this.planeMesh.material
      this.planeMesh.material = material

      const checkError = (where) => {
        const glError = context.getError()
        if (glError !== 0) {
          material.dispose()
          renderTarget.dispose()
          Sentry.captureEvent({
            message: 'GL Error in TAA check',
            breadcrumbs: [
                {
                    type: 'GL error data',
                    category: SENTRY_BREADCRUMB_CATEGORY,
                    data: {
                        errorCode: glError,
                        where,
                    }
                },
            ]
          })
          return true
        }
        return false
      }

      threeRenderer.setRenderTarget(renderTarget)
      if (checkError('setRenderTarget')) {
        return false
      }

      threeRenderer.clear()
      threeRenderer.render(this.scene, this.camera)
      if (checkError('render')) {
        return false
      }

      threeRenderer.setRenderTarget(currentRenderTarget)
      this.planeMesh.material = currentMaterial

      const bufferSize = size * size * 4
      const buffer = new Float32Array(bufferSize)
      threeRenderer.readRenderTargetPixels(renderTarget, 0, 0, size, size, buffer)
      if (checkError('readRenderTargetPixels')) {
        return false
      }

      let result = true
      for (let i = 0; i < bufferSize; i += 4) {
        if (buffer[i + 0] !== 1 ||
            buffer[i + 1] !== 0.5 ||
            buffer[i + 2] !== 0.25 ||
            buffer[i + 3] !== 1) {
              result = false
              break
            }
      }

      material.dispose()
      renderTarget.dispose()

      return result
    }
}